import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { cacheQueryData, getCachedQueryData } from '../utils/scoreQueue'
import {
  fetchSideBets,
  createSideBet,
  deleteSideBet,
  fetchCtpResults,
  upsertCtpResult,
  deleteCtpResult,
} from '../api/sideBets'

// ============================================
// Side Games
// ============================================

export function useSideBets(matchId) {
  const cacheKey = `sideBets_${matchId}`
  return useQuery({
    queryKey: ['sideBets', matchId],
    queryFn: async () => {
      const data = await fetchSideBets(matchId)
      cacheQueryData(cacheKey, data)
      return data
    },
    enabled: !!matchId,
    initialData: () => (matchId ? getCachedQueryData(cacheKey) : undefined),
    initialDataUpdatedAt: 0,
  })
}

export function useCreateSideBet() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createSideBet,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sideBets', data.match_id] })
    },
  })
}

export function useDeleteSideBet() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteSideBet,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sideBets'] })
    },
  })
}

// ============================================
// CTP Results
// ============================================

export function useCtpResults(gameId) {
  const cacheKey = `ctpResults_${gameId}`
  return useQuery({
    queryKey: ['ctpResults', gameId],
    queryFn: async () => {
      const data = await fetchCtpResults(gameId)
      cacheQueryData(cacheKey, data)
      return data
    },
    enabled: !!gameId,
    initialData: () => (gameId ? getCachedQueryData(cacheKey) : undefined),
    initialDataUpdatedAt: 0,
  })
}

export function useUpsertCtpResult() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: upsertCtpResult,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ctpResults', data.game_id] })
      queryClient.invalidateQueries({ queryKey: ['matchGames'] })
    },
  })
}

export function useDeleteCtpResult() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ gameId, holeNumber }) => deleteCtpResult(gameId, holeNumber),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ctpResults', variables.gameId] })
      queryClient.invalidateQueries({ queryKey: ['matchGames'] })
    },
  })
}
